/**
 * Spese › cosa paghiamo ogni mese, e a chi (26/10/2026, os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: dove vanno i soldi ogni mese?
 * La forma: i fornitori coi loro loghi (Google, Hostinger, Anthropic, Amazon…), raggruppati per
 * tipo di spesa, ogni gruppo col suo totale al mese; due colonne, così si vede tutto con un'occhiata.
 * Una riga si tocca per cambiarla; sospendere e terminare stanno nel ⋯. Le auto a rate sono un
 * gruppo come gli altri, con le rate che in banca ci sono e qui no. Sospese e terminate a richiesta.
 *
 * Prima (SpeseFisse.tsx, che resta nel codice): la frase, la riga "40 spese fisse · X al mese",
 * ricerca, ordina e filtra (per stato, categoria, modo di pagamento), tabelle a sei colonne che a
 * 1280 sbordavano. Tolti dalla vista: ricerca, ordina e filtra, la colonna "come si paga" e il
 * "prossimo addebito" (il giorno del mese dice già quando).
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { BrandLogo, brandFor } from '../../../components/ui/BrandLogo';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { NewRecurringModal } from '../../../components/modals/NewRecurringModal';
import { fmtEur } from '../../../lib/utils';
import { canWriteAdmin } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { CATEGORIA_SPESA_LABEL, OGNI_QUANTO, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, speseFisseQuery, type SpesaFissa, type SpeseFisseData } from '../fatture/fattureApi';
import { useAzioniSpesaFissa } from './SpeseFisse';

const ICONA_CATEGORIA: Record<string, IconName> = {
  SAAS: 'zap', AFFITTO: 'building', UTENZE: 'phone', AUTO: 'coins', CONSULENTI: 'users',
  COLLABORATORI: 'user', FORNITORI: 'brief', ALTRO: 'landmark',
};

/**
 * Il marchio: prima dal fornitore, poi dal nome della voce. L'ordine conta: "Google Workspace
 * Masamune" pagato a Google deve prendere il logo di Google, non il nostro.
 */
const marchio = (v: { counterparty: string | null; name: string }) => brandFor(v.counterparty) ?? brandFor(v.name);

/** "il 21", "il 14, ogni due mesi", "il 18 luglio": quando parte, detto corto. */
function quandoParte(v: SpesaFissa): string {
  if (v.cycle === 'YEARLY' && v.nextDueDate) return `${ilGiorno(v.nextDueDate).replace(/\s\d{4}$/, '')}`;
  if (!v.dayOfMonth) return OGNI_QUANTO[v.cycle];
  return v.cycle === 'MONTHLY' ? `il ${v.dayOfMonth}` : `il ${v.dayOfMonth}, ${OGNI_QUANTO[v.cycle]}`;
}

export function useSpeseFisse() {
  const q = useQuery(speseFisseQuery);
  const d = q.data;
  const gruppi = useMemo(() => {
    if (!d) return [];
    const inVigore = d.items.filter((v) => v.inVigore && !v.auto);
    return d.groups
      .map((g) => ({ ...g, voci: inVigore.filter((v) => v.category === g.category).sort((a, b) => b.alMese - a.alMese) }))
      .filter((g) => g.voci.length > 0)
      .sort((a, b) => b.monthly - a.monthly);
  }, [d]);
  const fornitori = useMemo(() => new Set((d?.items ?? []).filter((v) => v.inVigore).map((v) => (v.counterparty ?? v.name).toLowerCase())).size, [d]);
  return { ...q, gruppi, fornitori };
}

export function SpeseFisseLoghi() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const { data: d, isLoading, gruppi } = useSpeseFisse();
  const [modifica, setModifica] = useState<SpesaFissa | null>(null);
  const [ferme, setFerme] = useState(false);

  if (isLoading && !d) return <section className="ft-blocco"><p className="ft-vuoto">Carico le spese fisse…</p></section>;
  if (!d) return null;
  const sospese = d.items.filter((v) => !v.inVigore && !v.auto);

  // Le schede dei gruppi in due colonne, bilanciate per numero di righe: il gruppo più lungo
  // (i software) da una parte, gli altri dall'altra. Niente colonne CSS: i menu ⋯ restano sopra.
  const schede = [
    ...gruppi.map((g) => ({
      key: g.category,
      righe: g.voci.length + 1,
      nodo: (
        <div key={g.category} className="sp-gruppo">
          <div className="sp-gruppo-hd">
            <span className="sp-gruppo-ico"><Icon name={ICONA_CATEGORIA[g.category] ?? 'receipt'} size={14} /></span>
            <span className="t">{CATEGORIA_SPESA_LABEL[g.category] ?? g.category}</span>
            <span className="sp-gruppo-tot">€ {fmtEur(g.monthly)}<span className="u"> al mese</span></span>
          </div>
          {g.voci.map((v) => <RigaFissa key={v.id} v={v} canWrite={canWrite} onModifica={setModifica} />)}
        </div>
      ),
    })),
    ...(d.auto.length > 0 ? [{
      key: 'auto',
      righe: d.auto.length + 1 + d.rateMancanti.filter((m) => m.addebiti > 0).length,
      nodo: <AutoARate key="auto" d={d} canWrite={canWrite} onModifica={(id) => setModifica(d.items.find((v) => v.id === id) ?? null)} />,
    }] : []),
  ];
  const colonne: [typeof schede, typeof schede] = [[], []];
  let sinistra = 0;
  let destra = 0;
  for (const s of schede) {
    if (sinistra <= destra) { colonne[0].push(s); sinistra += s.righe; } else { colonne[1].push(s); destra += s.righe; }
  }

  return (
    <section className="ft-blocco" aria-label="Spese fisse">
      <h2 className="ft-h2"><Icon name="repeat" size={15} /> Spese fisse <span className="ft-h2-sub">quello che paghiamo ogni mese, IVA compresa</span></h2>
      <div className="sp-colonne">
        {colonne.map((c, i) => <div key={i} className="sp-colonna">{c.map((s) => s.nodo)}</div>)}
      </div>

      {sospese.length > 0 && (
        <>
          <button type="button" className="ft-mostra" onClick={() => setFerme((v) => !v)}>
            <Icon name={ferme ? 'eyeOff' : 'eye'} size={13} />
            {ferme ? 'Nascondi le spese fisse ferme' : `Mostra anche ${sospese.length === 1 ? 'la spesa fissa sospesa o terminata' : `le ${sospese.length} spese fisse sospese o terminate`}`}
          </button>
          {ferme && (
            <div className="sp-gruppo sp-gruppo--ferme">
              {sospese.map((v) => <RigaFissa key={v.id} v={v} canWrite={canWrite} onModifica={setModifica} ferma />)}
            </div>
          )}
        </>
      )}

      {modifica && createPortal(<NewRecurringModal recurring={modifica} onClose={() => setModifica(null)} />, document.body)}
    </section>
  );
}

function RigaFissa({ v, canWrite, onModifica, ferma }: { v: SpesaFissa; canWrite: boolean; onModifica: (v: SpesaFissa) => void; ferma?: boolean }) {
  const { sospendi, termina, riattiva } = useAzioniSpesaFissa();
  const menu: ActionItem[] = ferma
    ? [{ key: 'riattiva', label: 'Riattiva la spesa fissa', icon: 'repeat', onClick: () => riattiva.mutate(v), hidden: !canWrite }]
    : [
      { key: 'modifica', label: 'Modifica la spesa fissa', icon: 'pencil', onClick: () => onModifica(v), hidden: !canWrite },
      { key: 'sospendi', label: 'Sospendila per ora', icon: 'clock', onClick: () => sospendi.mutate(v), hidden: !canWrite },
      { key: 'termina', label: 'Terminala: non la paghiamo più', icon: 'x', danger: true, onClick: () => termina.mutate(v), hidden: !canWrite },
    ];
  // Il nome della voce dice già il fornitore quasi sempre ("Hostinger piano A"); sotto, solo quando aggiunge qualcosa.
  const sotto = v.counterparty && !v.name.toLowerCase().includes(v.counterparty.toLowerCase()) ? v.counterparty : null;
  return (
    <div className={`sp-voce${ferma ? ' is-ferma' : ''}`}>
      <BrandLogo brand={marchio(v)} name={v.counterparty ?? v.name} size={28} />
      <button type="button" className="sp-voce-chi" disabled={!canWrite} onClick={() => onModifica(v)} title={canWrite ? 'Modifica la spesa fissa' : undefined}>
        <span className="t">{v.name}{v.entity === 'PERSONALE_ANDREA' ? ' · di Andrea' : ''}</span>
        <span className="s">{ferma ? (v.endDate ? `terminata ${ilGiorno(v.endDate)}` : 'sospesa') : [sotto, quandoParte(v)].filter(Boolean).join(' · ')}</span>
      </button>
      <span className="sp-voce-cifra">
        € {fmtEur(v.amountGross, true)}
        {v.cycle !== 'MONTHLY' && <span className="u">{OGNI_QUANTO[v.cycle]}</span>}
      </span>
      <ActionsMenu items={menu} label={`Azioni su ${v.name}`} />
    </div>
  );
}

/** Le auto: la finanziaria ha già pagato il fornitore, noi paghiamo la rata (era "Auto pagate a rate"). */
function AutoARate({ d, canWrite, onModifica }: { d: SpeseFisseData; canWrite: boolean; onModifica: (recurringId: string) => void }) {
  const { creaRateMancanti } = useAdminActions();
  const mancanti = d.rateMancanti.filter((m) => m.addebiti > 0);
  return (
    <div className="sp-gruppo">
      <div className="sp-gruppo-hd">
        <span className="sp-gruppo-ico"><Icon name="coins" size={14} /></span>
        <span className="t">Auto a rate</span>
        <span className="sp-gruppo-tot">€ {fmtEur(d.totals.autoMonthly)}<span className="u"> al mese</span></span>
      </div>
      {d.auto.map((a) => {
        const rec = a.recurring;
        return (
          <div key={a.payableId} className="sp-voce">
            <BrandLogo brand={rec ? marchio(rec) : null} name={rec?.counterparty ?? a.vendorName} size={28} />
            <button type="button" className="sp-voce-chi" disabled={!canWrite || !rec} onClick={() => rec && onModifica(rec.id)}>
              <span className="t">{rec ? (rec.counterparty ?? rec.name) : 'Rata non collegata'}</span>
              <span className="s">auto di {a.vendorName}, finanziati € {fmtEur(a.financed)}</span>
            </button>
            <span className="sp-voce-cifra">{rec ? `€ ${fmtEur(rec.monthly, true)}` : '—'}</span>
            <span />
          </div>
        );
      })}
      {mancanti.map((m) => (
        <div key={m.recurringId} className="sp-mancanti">
          <Icon name="alert" size={13} />
          <span className="min-w-0">
            In banca ci sono {m.addebiti === 1 ? '1 rata' : `${m.addebiti} rate`} di {m.nome} che qui mancano (€ {fmtEur(m.totaleAddebiti)}).
          </span>
          {canWrite && (
            <button className="btn btn-sm" disabled={creaRateMancanti.isPending}
              onClick={() => creaRateMancanti.mutate({ recurringId: m.recurringId, nome: m.nome, addebiti: m.addebiti, totale: m.totaleAddebiti })}>
              <Icon name="plus" size={10} /> Aggiungile
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
